import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import CircularProgress from '@material-ui/core/CircularProgress';
import { fetchResponseMessages, invokeChangeResponseMessage, ResponseMessage, } from "../api-client/client"
import Grid from "@material-ui/core/Grid";
import ErrorMessage from "../components/ErrorMessage";
import MessageIcon from '@material-ui/icons/Message';
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from '@material-ui/icons/Edit';
import Button from "@material-ui/core/Button";
import Grow from "@material-ui/core/Grow";
import Card from "@material-ui/core/Card";
import CardHeader from "@material-ui/core/CardHeader";
import CardContent from "@material-ui/core/CardContent";

const ResponseMessagesPage: React.FC = () => {
    const [responseMessages, setResponseMessages] = useState<ResponseMessage[]>([]);
    const [errorMsg, setErrorMsg] = useState<string>();
    const [currentlyEditing, setCurrentlyEditing] = useState<ResponseMessage | null>(null);
    const [updateOngoing, setUpdateOngoing] = useState<boolean>(false);
    const [initialized, setInitialized] = useState<boolean>(false);

    useEffect(() => {
        fetchResponseMessages().then(fetchedResponseMessages => {
            setResponseMessages(fetchedResponseMessages)
            setInitialized(true);
        }).catch(error => {
            setErrorMsg(error.message);
        });
    }, [setResponseMessages]);

    const handleEditClick = (responseMessage: ResponseMessage) => {
        setCurrentlyEditing(responseMessage)
    };

    const handleSaveClick = (newResponseMessage: ResponseMessage) => {
        setUpdateOngoing(true)
        invokeChangeResponseMessage(newResponseMessage).then(() => {
            setResponseMessages(responseMessages.map((msg) =>
                msg.reason === newResponseMessage.reason ? newResponseMessage : msg));
            setUpdateOngoing(false)
            setCurrentlyEditing(null);
        }).catch(error => {
            setErrorMsg(error.message);
        });
    };

    return (
        <Box mt={4}>
            <Paper>
                <Box p={3} pt={4}>
                    <Typography component="h2" variant="h5" color="primary" gutterBottom>
                        Response messages configuration
                    </Typography>
                    <Typography variant={"body2"}>
                        On this page you can view and edit the messages and URLs shown to users for different kind of
                        error conditions that occur during verification of user access.
                    </Typography>
                    <Box pt={2}>
                    { errorMsg ? <ErrorMessage message={errorMsg}/> : (
                        initialized ? (
                            <React.Fragment>
                                    {responseMessages.map(row => (
                                        <Box py={1} key={row.reason}>
                                        <Card raised={currentlyEditing != null && currentlyEditing.reason === row.reason}>
                                            <CardHeader
                                                avatar={
                                                    <MessageIcon/>
                                                }
                                                title={<Typography variant={"h6"}>{row.reasonDescription}</Typography>}
                                                subheader={row.reason}
                                                action={
                                                    <div>
                                                        { currentlyEditing != null || (
                                                            <IconButton hidden={ currentlyEditing != null } onClick={ () => handleEditClick(row) }>
                                                                <EditIcon data-testid={ row.reason + "-edit-button" }/>
                                                            </IconButton>) }
                                                    </div>
                                                }
                                            />
                                            <CardContent>
                                                <Box mt={-2.5}>
                                                { currentlyEditing != null && currentlyEditing.reason === row.reason ? (
                                                        <React.Fragment>
                                                            <Box pt={0}>
                                                                <TextField
                                                                    variant="standard"
                                                                    label="Message"
                                                                    value={currentlyEditing.message}
                                                                    onChange={(event) => { setCurrentlyEditing({...currentlyEditing, message: event.target.value}) }}
                                                                    fullWidth multiline={true}
                                                                    inputProps={{"data-testid": row.reason + "-message"} as InputHTMLAttributes<HTMLInputElement>}
                                                                />
                                                            </Box>
                                                            <Box pt={1}>
                                                                <Grid container>
                                                                    <Grid item xs={ 9 }>
                                                                        <TextField variant="standard"
                                                                                   label="Url"
                                                                                   value={ currentlyEditing.responseUrl }
                                                                                   fullWidth
                                                                                   inputProps={ { "data-testid": row.reason + "-url" } as InputHTMLAttributes<HTMLInputElement> }
                                                                                   onChange={ (event) => {
                                                                                       setCurrentlyEditing({
                                                                                           ...currentlyEditing,
                                                                                           responseUrl: event.target.value
                                                                                       })
                                                                                   } }/>
                                                                    </Grid>
                                                                    <Grid item xs={ 3 }>
                                                                        <Grid container justifyContent={ "flex-end" }>
                                                                            <Grow in={ currentlyEditing.reason === row.reason }>
                                                                                <Box pt={ 1 }>
                                                                                    <Button variant={ "contained" } color={ "primary" }
                                                                                            disabled={ updateOngoing }
                                                                                            onClick={ () => handleSaveClick(currentlyEditing as ResponseMessage) }
                                                                                            data-testid={ row.reason + "-save-button" }>Save</Button>
                                                                                    <Box ml={ 1 } component="span">
                                                                                        <Button variant={ "text" }
                                                                                                onClick={ () => setCurrentlyEditing(null) }>Cancel</Button>
                                                                                    </Box>
                                                                                </Box>
                                                                            </Grow>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>
                                                        </React.Fragment>)
                                                    : (
                                                        <React.Fragment>
                                                            <Box pt={ 0 }>
                                                                <TextField variant="standard"
                                                                           label="Message"
                                                                           value={ row.message }
                                                                           fullWidth multiline={ true }
                                                                           InputProps={ { disableUnderline: true, readOnly: true } }
                                                                           inputProps={ { "data-testid": row.reason + "-message" } }
                                                                           disabled={ currentlyEditing != null }/>
                                                            </Box>
                                                            <Box pt={ 2 } mt={ -1 }>
                                                                <TextField variant="standard" label="Url" value={ row.responseUrl } fullWidth
                                                                           InputProps={ { disableUnderline: true, readOnly: true } }
                                                                           inputProps={ { "data-testid": row.reason + "-url" } }
                                                                           disabled={ currentlyEditing != null }/>
                                                            </Box>
                                                        </React.Fragment>
                                                    ) }
                                                </Box>
                                            </CardContent>
                                        </Card>
                                        </Box>
                                    )) }
                            </React.Fragment>
                        ) : <Grid container justifyContent={ "center" }><Box pt={ 3 }><CircularProgress/></Box></Grid>) }
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default ResponseMessagesPage;
